import React, { ChangeEvent } from "react";
import * as ATCAConstants from '../util/ATCAConstants';
import * as Utils from '../util/Utils'
import { grey } from '@mui/material/colors';

import { Dialog, DialogTitle, DialogContent, DialogActions,  Button, Box, 
  Typography, Snackbar, Alert, TextField, Tooltip, Chip, FormControl, 
  InputLabel, MenuItem, ListItemText, Select, SelectChangeEvent, FormHelperText,
  Accordion, AccordionSummary, AccordionDetails} from "@mui/material";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Stack } from "@mui/system";
import { get, post } from 'aws-amplify/api';
import { useDropzone } from 'react-dropzone';
import BandInfoDialog from "./BandInfoDialog";

export default function ScheduleConverterDialog(
  props: {
    open: boolean,
    handleCancel: () => void,
  }
) {
  const [legacyContent, setLegacyContent] = React.useState('');
  const [scheduleContent, setScheduleContent] = React.useState('');
  const [scheduleDetail, setScheduleDetail] = React.useState<any>({});
  const [correlatorConfig, setCorrelatorConfig] = React.useState<any>({
    band_setting: 'midlow'
  });
  const [snackMessage, setSnackMessage] = React.useState<any>(null);
  const [correlatorSettings, setCorrelatorSettings] = React.useState<any[]>([]);
  const [showReceiverInfo, setShowReceiverInfo] = React.useState<boolean>(false);
  const [enableConvert, setEnableConvert] = React.useState<boolean>(false);

  const { getRootProps, getInputProps, open } = useDropzone
    ({maxFiles: 1, 
      multiple: false,
      accept: { 'text/plain': ['.sch']},
      onDrop: acceptedFiles => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e && e.target) {
            const text = e.target.result as string;
            setLegacyContent(text);
            setScheduleContent('');
          }
        }
        reader.readAsText(acceptedFiles[0]);
      }
    });
  
  React.useEffect(() => {
    setLegacyContent('');
    setScheduleContent('');
    setScheduleDetail({});
    setCorrelatorConfig({band_setting: 'midlow'});
  }, [props.open]);

  
  React.useEffect(() => {
    const result = Utils.validateFreq(correlatorConfig);
    const freqConfig = {
      ...correlatorConfig,
      ...result
    };
    setCorrelatorConfig(freqConfig);
  }, [correlatorConfig['centreFreq'], correlatorConfig['band_setting']]);

  React.useEffect(() => {
    if (!legacyContent) {
      setEnableConvert(false);
      return;
    }

    if (!correlatorConfig['centreFreq']) {
      setEnableConvert(false);
      return;
    }

    if (!correlatorConfig['band_setting']) {
      setEnableConvert(false);
      return;
    }

    if (!correlatorConfig['correlator_setting']) {
      setEnableConvert(false);
      return;
    }

    setEnableConvert(true);
  }, [correlatorConfig, legacyContent]);

  React.useEffect(() => {
    const getSettingsOperation = get(
      {
        apiName: 'cabb',
        path: '/get_correlator_settings',
        options: {
          queryParams: {
            integration_time: `${ATCAConstants.DEFAULT_INTEGRATION_TIME_S}`
          }
        }
      }
    );

    getSettingsOperation.response
      .then(response => response.body.json())
      .then((response: any) => {        
        if (response['status'] === 'fail') {
          let message = response['message'] || '';
          setSnackMessage({
            severity: 'error',
            message: 'Could not retrieve setting from server: ' + message
          });
        } else {
          setCorrelatorSettings(response);
        }
      })
      .catch((err: Error) => {
        setSnackMessage({
          severity: 'error',
          message: 'Could not retrieve correlator settings from server!'
        });
      });
  }, []);

  const handleCancel = () => {
    props.handleCancel();
  }

  const handleScheduleDetail = 
    (value: any, fieldName: string) => {

    const newScheduleDetail = {
      ...scheduleDetail,
      [fieldName]: value
    };

    setScheduleDetail(newScheduleDetail);
  }

  const handleReceiverChange = (event: SelectChangeEvent) => {
    const newConfig = {...correlatorConfig};
    const receiverName = event.target.value;
    newConfig['receiver'] = receiverName;

    const receivers = ATCAConstants.ReceiverRange.filter(
      (rec) => {return rec['name'] === receiverName}
    )
    const receiver = receivers[0];

    // set default values for this receiver
    newConfig['band_setting'] = receiver['default_band_setting'];
    newConfig['centreFreq'] = Utils.getDefaultCentreFreq(
      receiver, 
      newConfig['band_setting'], 
    );

    setCorrelatorConfig(newConfig);
  }

  const handleBandSettingChange = (event: SelectChangeEvent) => {
    const newConfig = {...correlatorConfig};
    const bandSetting = event.target.value as string;
    const receiverName = newConfig['receiver'];
    newConfig['band_setting'] = bandSetting;

    if ( receiverName === '4cm') {
      const receiver = ATCAConstants.get4cmReceiver();
      newConfig['centreFreq'] = Utils.getDefaultCentreFreq(
        receiver,
        bandSetting, 
      );
    }
    setCorrelatorConfig(newConfig);
  }
  
  const setFreqConfigValue = (event: ChangeEvent<HTMLInputElement>) => {
    const newConfig = {
      ...correlatorConfig,
      'centreFreq': event.target.value
    };

    setCorrelatorConfig(newConfig);
  }
  
  const openReceiverInfo = () => {
    setShowReceiverInfo(true);
  };

  const closeReceiverInfo = () => {
    setShowReceiverInfo(false);
  };

  function handleCorrelatorSettingChange(event: SelectChangeEvent<any>) {
    const newCorrConfig = {
      ...correlatorConfig,
      correlator_setting: event.target.value
    }

    setCorrelatorConfig(newCorrConfig);
  }

  function convertLegacy() {
    const body: any = {}

    if (legacyContent) {
      body['legacy_schedule'] = legacyContent.split('\n');
    }

    const correlator_setting_name = correlatorConfig['correlator_setting'];
    if (correlator_setting_name) {
      body['correlator_setting_name'] = correlator_setting_name;
    }

    const centreFreq = correlatorConfig['centreFreq'];
    body['centreFreq'] = Number(centreFreq);

    const bandSetting = correlatorConfig['band_setting'];
    body['band_setting'] = bandSetting;

    const observer = scheduleDetail['observer'];
    if (observer) {
      body['observer'] = observer;
    }

    const owner = scheduleDetail['owner'];
    if (owner) {
      body['owner'] = owner;
    }

    const proposal_code = scheduleDetail['proposal_code'];
    if (proposal_code) {
      body['proposal_code'] = proposal_code;
    }

    const postOperation = post(
      {
        apiName: 'cabb',
        path: '/convert_legacy_schedule',
        options: {
          body: body,
          headers: {'content-type': 'application/json'}
        }
      }
    );

    postOperation.response
      .then(response => response.body.json())
      .then((response: any) => {
        if (response['status'] === 'success') {
          // Handle success
          setScheduleContent(response['content']);
        } else {
          setScheduleContent(response);
        }
      });
  }

  const  handleDownload = () => {
    let filename = 'schedule.json';
    var element = document.createElement('a');
    var content = scheduleContent ? JSON.stringify(scheduleContent, null, 4) : '';

    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + content);
    element.setAttribute('download', filename);

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Dialog open={props.open} PaperProps={{ sx: {height: '80%'} }}
      aria-labelledby="convert-schedule-dialog-title"
      onClose={handleCancel}
      maxWidth="lg" fullWidth>
              
      <BandInfoDialog open={showReceiverInfo} handleClose={closeReceiverInfo}></BandInfoDialog>
        
      <DialogTitle id="convert-schedule-dialog-title">
        Convert Legacy Schedule File
      </DialogTitle>
      <DialogContent sx={{height: '100%'}}>
        <Stack spacing={2} sx={{ height: '100%',  display: 'flex'}}>
          <Stack direction={'row'} alignItems={'flex-end'} spacing={2}
            justifyContent={'space-between'}>
            <div {...getRootProps({ 
              className: 'dropzone',
              'aria-label': 'drag and drop a convert legacy file',
              })}>
              <input {...getInputProps()} />
                <Button>
                  <Typography variant="caption" textAlign='center'>
                    Drag 'n drop ONE json file here, or click to select a file.
                  </Typography>
                </Button>
            </div>

            <Button variant="contained" onClick={convertLegacy}
              disabled={!enableConvert} size="large">
                Convert
            </Button>

            <Button variant="contained" onClick={handleDownload}
              disabled={!Boolean(scheduleContent)} size="large">
                Download
            </Button>
          </Stack>

          <Accordion defaultExpanded>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Over Rides</Typography>
            </AccordionSummary>

            <AccordionDetails>

              <Stack direction={'row'} spacing={2} sx={{ alignItems: 'baseline'}}>

                <FormControl variant="standard" sx={{ width: '20%' }}>
                  <InputLabel id="receiver">Receiver</InputLabel>
                  <Select
                    labelId="receiver-label"
                    id="receiver-select-value"
                    label="Receiver"
                    onChange={handleReceiverChange}
                    value={(correlatorConfig['receiver'])||''}
                    renderValue={(selected) => selected}
                    displayEmpty={true}
                  >
                    {ATCAConstants.ReceiverRange
                    .map((receiver: any) => (
                      <MenuItem key={receiver['name']} value={receiver['name']}>
                        <ListItemText primary={receiver['name']}
                          secondary={
                            <Typography variant="body2">
                              { receiver['start'] + ' - ' + receiver['end'] + ' MHz'}
                            </Typography>
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField required variant="standard" 
                  label="Centre Freq (MHz)" sx={{ height: '80px', width: '20%' }}
                  id='centre_freq'
                  value={(correlatorConfig['centreFreq'])||''}
                  error={!Boolean(correlatorConfig['isValid'])}
                  helperText={correlatorConfig['message'] }
                  onChange={setFreqConfigValue} 
                  disabled={(
                    !correlatorConfig['receiver']
                    ||
                    (correlatorConfig['receiver'] === '4cm')
                  )}/>

                <FormControl required variant="standard" sx={{ width: '30%' }}>
                  <InputLabel id="band-config">Band Setting</InputLabel>
                  <Select
                    labelId="band-setting-label"
                    id="band-setting-select-value"
                    label="Band Setting"
                    onChange={handleBandSettingChange}
                    value={correlatorConfig['band_setting']||''}
                    error={!Boolean(correlatorConfig['band_setting'])}
                    renderValue={(selected) => selected}
                    displayEmpty={true}
                  >
                    {ATCAConstants.BAND_SETTINGS
                      .filter((setting: any) => {return (correlatorConfig['receiver'] && setting['compatible_receivers'][correlatorConfig['receiver']])})
                      .map((setting: any) => (
                      <MenuItem key={setting['name']} value={setting['name']}
                        disabled={setting['status'] === 'obsolete'}
                      >
                        <ListItemText primary={
                            setting['name'] + 
                            (setting['status'] === 'obsolete' ? ' (obsolete)' : '')
                          }
                          secondary={
                            <Stack width={'100%'} direction="row" spacing={2}>
                              <Typography variant="body2">
                                { setting['band1_offset'] >=0 ? setting['band1_offset'] : '-' }
                              </Typography>
                              <Typography variant="body2">
                                { setting['band2_offset'] >=0 ? setting['band2_offset'] : '-' }
                              </Typography>
                              <Typography variant="body2">
                                { setting['band3_offset'] >=0 ? setting['band3_offset'] : '-' }
                              </Typography>
                              <Typography variant="body2">
                                { setting['band4_offset'] >=0 ? setting['band4_offset'] : '-' }
                              </Typography>
                            </Stack>
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    { correlatorConfig['band_setting'] ? '' : 'Select a Band Setting' }
                  </FormHelperText>
                </FormControl>

                <FormControl required sx={{ width: '40%' }} variant="standard">
                  <InputLabel id="cor-config">Correlator setting</InputLabel>
                  <Select
                    labelId="cor-config-label"
                    id="cor-config-select-value"
                    label="Correlator setting"
                    onChange={handleCorrelatorSettingChange}
                    value={correlatorConfig['correlator_setting']||''}
                    error={!Boolean(correlatorConfig['correlator_setting'])}
                    renderValue={(selected) => selected}
                    displayEmpty={true}
                  >
                    {correlatorSettings.filter(
                      (setting: any) => {
                        const receiver = correlatorConfig['receiver'];
                        const zooms = setting['zoom'] || [];

                        return (setting['compatible_receivers'][receiver]) 
                          && (zooms.length === 0);
                      }
                    ).map((config: any) => (
                      <MenuItem key={config['name']} value={config['name']}
                        disabled={config['status'] === 'obsolete'}
                      >
                        <ListItemText primary={
                          config['name'] + 
                          (config['status'] === 'obsolete' ? ' (obsolete)' : '')
                        }
                          secondary={
                            <Stack width={'100%'} direction="row" spacing={2}>
                              <Typography variant="body2">
                                {config['spectral_points_per_subband']} points  
                              </Typography>
                              <Typography variant="body2">
                                {Math.round(config['spectral_resolution']*1000)/1000}{config['spectral_resolution_unit']}
                              </Typography>
                              <Typography variant="body2">
                                {config['data_rate']/1000}MB/sec
                              </Typography>
                            </Stack>
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    { correlatorConfig['correlator_setting'] ? '' : 'Select a Correlator Setting' }
                  </FormHelperText>
                </FormControl>

                <Tooltip title="View receiver help">
                  <Chip label={'?'} size="medium" 
                    key={`freq-receiver`} onClick={openReceiverInfo}
                    sx={{color: 'black', backgroundColor: grey[100]}}/>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={2} alignItems='flex-end'>
                <TextField id="observer" label="Observer"
                  value={scheduleDetail['observer'] || ''}
                  onChange={(e) => handleScheduleDetail(e.target.value as string, 'observer')}
                  variant="standard" sx={{ flexGrow: 1 }}/>

                <TextField id="proposal-code" label="Proposal Code" 
                  value={scheduleDetail['proposal_code'] || ''}
                  onChange={(e) => handleScheduleDetail(e.target.value as string, 'proposal_code')}
                  variant="standard" sx={{ flexGrow: 1 }} />
                  
                <TextField id="schedule-owner" label="Schedule Owner" 
                  value={scheduleDetail['owner'] || ''}
                  onChange={(e) => handleScheduleDetail(e.target.value as string, 'owner')}
                  variant="standard" sx={{ flexGrow: 1 }} />
              </Stack>

            </AccordionDetails>
          </Accordion>

          <Stack direction={'row'} spacing={2} sx={{flexGrow: 4}}>
            <Box
              sx={{ 
                border: '1px solid #bdbdbd',
                borderRadius: '10px',
                padding: '10px',
                height: 'calc(100% - 20px)',
                overflow: 'auto',
                width: '50%'
              }}
            >
              <pre style={{width:'100%', height: '175x'}}>
                {legacyContent}
              </pre>
            </Box>

            <Box
              sx={{
                border: '1px solid #bdbdbd',
                borderRadius: '10px',
                padding: '10px',
                height: 'calc(100% - 20px)',
                overflow: 'auto',
                width: '50%'
              }}
            >
              <pre style={{width:'100%', height: '175px'}}>
                { scheduleContent ? JSON.stringify(scheduleContent, null, 4) : ''}
              </pre>
            </Box>
          </Stack>

        </Stack>
      </DialogContent>

      <DialogActions>
          <Button onClick={handleCancel}>
            Close
          </Button>
      </DialogActions>

      <Snackbar
        role = "alert"
        aria-label = {"Alert message"}
        open={Boolean(snackMessage)}
        autoHideDuration={6000}
        onClose={e => setSnackMessage(null)}
      >
        <Alert severity={snackMessage ? snackMessage.severity : 'error' }>
          {snackMessage ? snackMessage.message : ''}
        </Alert>
      </Snackbar>

    </Dialog>
  );
};

